import React, { useContext } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Trans } from '@lingui/react';
import Hero from '@ecosystems/landing_pages/hero/Hero';
import {
  TextWrapper,
  Title,
  SubTitle,
  TextWrapperInner,
  VideoWrapper,
  ActionsWrapper,
  Disclaimer,
} from '@ecosystems/landing_pages/hero/components';
import Button from '@components/buttons';
import { Container } from '@components/layout/Grid';
import { Tag, Tags } from '@organisms/video-player';
import SquaresGrid from '@ecosystems/homepage/SquaresGrid';
import FourthSection from '@ecosystems/homepage/FouthSection';
import FifthSection from '@ecosystems/homepage/FifthSection';
import TestimonialsSection from '@ecosystems/homepage/TestimonialsSection';
import MetaTags from '@components/MetaTags';
import MobileAppSection from '@ecosystems/homepage/MobileAppSection';
import SvgCheckmark from '@assets/Checkmark';
import Trailer from '@organisms/video-player/Trailer';
import { Video } from '@gql/generated';
import { UserContext } from '@lib/contexts/UserProvider';
import PricingSection from '@ecosystems/pricing/PricingSection';
import { FLAGS_QUERY, names } from '@lib/hooks/useFlags';

const HeroSection = styled(Hero)`
  position: relative;
  min-height: 80vh;

  ${TextWrapper} {
    width: 100%;
    padding: 0 20px;
    background-color: rgba(102, 124, 145, 0.6);

    ${TextWrapperInner} {
      align-items: center;
      font-size: 12px;
      padding: 10vh 0 0;
      margin: 0 auto;
    }

    ${Title} {
      color: white;
      font-size: 1.6em;
      font-weight: 400;
      line-height: 1.3;
      text-align: center;
    }

    ${SubTitle} {
      color: white;
      font-size: 3em;
      font-weight: 500;
      line-height: 1.2;
      text-align: center;
    }

    ${ActionsWrapper} a {
      font-size: calc(1em + 2px);
      font-weight: 500;
      padding: 0.6em 1em;
    }

    ${Disclaimer} {
      font-style: normal;
      margin-top: 30px;

      ul {
        display: flex;
        flex-direction: column;
        align-items: center;
        list-style: none;
        color: white;
        font-size: 16px;
        font-weight: 600;
        padding-left: 0;

        li {
          display: flex;
          align-items: center;
          margin-top: 10px;

          svg {
            fill: white;
            height: 14px;
            margin-right: 10px;
            margin-top: 4px;
          }
        }
      }
    }
  }

  ${VideoWrapper} {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    min-height: 100%;

    img {
      object-fit: cover;
      object-position: center;
    }
  }

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    height: 70vh;

    ${TextWrapper} {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      padding: 0 40px;

      ${TextWrapperInner} {
        max-width: 70%;
        font-size: 2vw;
        padding-top: 0;

        ${Title} {
          font-size: 1.4em;
        }

        ${ActionsWrapper} a {
          font-size: 0.9em;
        }

        ${Disclaimer} {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          font-style: normal;
          margin-bottom: 0;
          padding: 14px 20px;
          background-color: rgba(17, 158, 221, 0.2);

          ul {
            max-width: 860px;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            margin: 0 auto;

            li {
              margin-top: 0;
              margin-right: 50px;
            }
          }
        }
      }
    }

    ${VideoWrapper} {
      position: static;
    }
  }

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    ${TextWrapper} {
      ${TextWrapperInner} {
        font-size: 1.4vw;

        ${ActionsWrapper} a {
          font-size: 0.8em;
        }
      }
    }
  }
`;

const Description = styled.div``;

const SecondSectionHero = styled(Hero)`
  ${TextWrapper} {
    padding-bottom: 20px;
  }

  ${Title} {
    font-size: 26px;
    font-weight: 500;
    margin-bottom: 10px;
  }

  ${Description} {
    flex: 1;
    display: flex;
    flex-direction: column;

    p {
      flex: 1;
      font-size: 18px;
      line-height: 1.4;
    }
  }

  ${Tags} {
    top: 8px;
    left: 0;

    ${Tag} {
      text-transform: uppercase;
      border-radius: 0;
      padding: 8px;
      background-color: #56b9e5;
    }
  }

  ${VideoWrapper} {
    position: relative;
    display: flex;
    align-items: center;
  }
`;

const SixthSection = styled(Hero)`
  p,
  h1,
  h3,
  span {
    color: white;
  }

  padding: 0 20px;
  background-color: #5b96b7;

  ${TextWrapper} {
    padding-bottom: 10px;
    margin: 0 auto;
  }

  ${TextWrapperInner} {
    align-items: center;
    text-align: center;

    ${Title} {
      font-size: 20px;
      font-weight: 400;
      margin-bottom: 20px;
    }

    ${SubTitle} {
      font-size: 24px;
      line-height: 1.4;
    }

    button {
      color: #5b96b7;
      border-radius: 50px;
      margin-bottom: 20px;
    }

    ${Disclaimer} {
      font-size: 16px;
      margin-top: 20px;
    }
  }

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    ${TextWrapperInner} {
      ${SubTitle} {
        font-size: 34px;
      }
    }
  }
`;

const Anchor = styled.a`
  && {
    color: inherit;
    text-decoration: none;
  }
`;

const trailer = {
  id: '1e73f7ef-06b3-4235-a580-7215f88a38f8',
  content: { title: 'Home video trailer' },
  identifier: '2z3h',
  thumbnailUrl:
    'https://yogobe.23video.com/64968568/65791722/be188418308c9c0b595a975ee9d28e26/large/embed/thumbnail.jpg',
  hls:
    'https://yogobe.23video.com/64968568/65791722/664f16a0fa65c1f764aa85f78a076e9f/video_hls/playlist.m3u8',
  player: {
    hasAccess: true,
    assets: [
      {
        src:
          'https://yogobe.23video.com/64968568/65791722/664f16a0fa65c1f764aa85f78a076e9f/video_mobile_high/yogobestartpagetrailers1920x1080ons-video.mp4',
        resolution: 180,
        size: 2043187,
      },
      {
        src:
          'https://yogobe.23video.com/64968568/65791722/664f16a0fa65c1f764aa85f78a076e9f/video_medium/yogobestartpagetrailers1920x1080ons-video.mp4',
        resolution: 360,
        size: 4189153,
      },
      {
        src:
          'https://yogobe.23video.com/64968568/65791722/664f16a0fa65c1f764aa85f78a076e9f/video_hd/yogobestartpagetrailers1920x1080ons-video.mp4',
        resolution: 720,
        size: 6327752,
      },
      {
        src:
          'https://yogobe.23video.com/64968568/65791722/664f16a0fa65c1f764aa85f78a076e9f/video_1080p/yogobestartpagetrailers1920x1080ons-video.mp4',
        resolution: 1080,
        size: 12754101,
      },
    ],
  },
} as Video;

const Homepage = ({ currentUser: _currentUser }) => {
  const router = useRouter();
  const { lang } = router.query;
  const [currentUser] = useContext(UserContext);

  const user = currentUser ?? _currentUser;

  return (
    <>
      <MetaTags
        title="Yoga och träning online för hållbar hälsa | YOGOBE"
        description="Yoga & träna online med några av Nordens främsta instruktörer. Digital yoga, träning, stresshantering och mer - var du vill, när du vill."
      />
      <HeroSection
        image="/static/landing-hero-2.jpeg"
        title={<Trans id="landingpage.hero.title" />}
        subtitle={<Trans id="landingpage.hero.subtitle" />}
        actions={
          !user?.id
            ? [
                <Button
                  key="0"
                  appearance="default-blue"
                  href={`/${lang}/register/account`}
                >
                  {<Trans id="landingpage.hero.cta" />}
                </Button>,
              ]
            : null
        }
        disclaimer={
          <ul>
            <li>
              <SvgCheckmark /> <Trans id="landingpage.hero.bullet1" />
            </li>
            <li>
              <SvgCheckmark />
              <Trans id="landingpage.hero.bullet2" />
            </li>
            <li>
              <SvgCheckmark />
              <Trans id="landingpage.hero.bullet3" />
            </li>
          </ul>
        }
      />
      <Container flexDirection={'column'} px={[25, 20, 20]} py={40}>
        <SecondSectionHero
          title={<Trans id="landingpage.section2.title" />}
          description={
            <Description>
              <p>{<Trans id="landingpage.section2.desc" />}</p>
              <Link href={`/${lang}/videos/genres/all`} legacyBehavior>
                <Button appearance="link" href={`/${lang}/videos/genres/all`}>
                  {<Trans id="landingpage.section2.link" />} &gt;
                </Button>
              </Link>
            </Description>
          }
          video={
            <Trailer
              {...{
                video: trailer,
                thumbnailImage: trailer?.thumbnailUrl,
                isLimited: false,
                autoPlay: false,
                videoSources: trailer?.player?.assets,
                elementID: 'video1',
                tags: [<Trans key="0" id="landingpage.section2.trailer_tag" />],
              }}
            />
          }
          reverseColumns
        />
      </Container>
      <SquaresGrid />
      <FourthSection />
      <FifthSection />
      {!user?.id ? (
        <SixthSection
          title={<Trans id="landingpage.section6.title" />}
          subtitle={<Trans id="landingpage.section6.subtitle" />}
          actions={[
            <Button
              key="0"
              appearance="default-white"
              href={`/${lang}/register/account`}
            >
              {<Trans id="landingpage.section6.cta" />}
            </Button>,
          ]}
          disclaimer={<Trans id="landingpage.section6.disclaimer" />}
        />
      ) : null}
      <TestimonialsSection />
      <PricingSection />
      <MobileAppSection />
    </>
  );
};

export const getServerSideProps = async ctx => {
  const { apolloClient } = ctx.req;
  //
  return {
    props: {},
  };
};

export default Homepage;
